/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable security/detect-object-injection */
import { ArrayExtensions, generateImageUrl, IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { DeliveryOption, ItemAvailability, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICommerceApiSettings, ICoreContext, IGridSettings, Image } from '@msdyn365-commerce/core';
import {
    IDobbiesBuyboxStoreSelectorConfig,
    IDobbiesBuyboxStoreSelectorResources
} from '../dobbies-buybox-store-selector.props.autogenerated';
import {
    IStoreSelectorLocationLineItemResources,
    StoreSelectorLocationLineItemComponent
} from './dobbies-buybox-store-selector-location-line-item';
import { _getScreenSize } from '../../../themes/dobbies/utilities/display-utils';

export interface IStoreSelectorLocationLinesProps {
    locations?: IFullOrgUnitAvailability[];
    resources: IDobbiesBuyboxStoreSelectorResources;
    preferredDistanceUnit?: string;

    alreadySelectedLocation?: OrgUnitLocation;
    outOfStockThreshold: number;
    enableOutOfStockCheck: boolean;
    storeLocatorView?: boolean;
    selectedStoreLocationId?: string;
    isPreferredStoreEnabled?: boolean;
    showQuantity?: boolean;
    displayList?: boolean;
    isLocationDisabled?: boolean;
    productPickUpDeliveryOptions?: DeliveryOption[];
    onClick: (locationId: string | undefined) => void;
    preferredStoreLocationId: string | null;
    onSetAsPreferredStore: (locationId: string | undefined) => void;
    onRemovePreferredStore: () => void;
    filteredPickupMode?: string;
    isDisplayProduct: boolean;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    onLocationSelected(location: OrgUnitLocation | undefined): void;
    sortByLocation?: boolean;
    showCnC?: boolean;
    pickupStoreIds: (number | undefined)[];
    storeHeadingForLocalStore: string;
    config: IDobbiesBuyboxStoreSelectorConfig;
    apiSettings: ICommerceApiSettings;
    gridSettings?: IGridSettings;
    context?: ICoreContext;
}

/**
 * Displays all the location line items, or null if none exists.
 */
@observer
export class StoreSelectorLocationLines extends React.PureComponent<IStoreSelectorLocationLinesProps> {
    private readonly selectedLocationRef: React.RefObject<HTMLDivElement>;

    @observable private _stores: IFullOrgUnitAvailability[] | undefined;

    private storeCounter: number;
    public listRef: React.RefObject<HTMLDivElement>;
    public constructor(props: IStoreSelectorLocationLinesProps) {
        super(props);
        this.selectedLocationRef = React.createRef();
        this.storeCounter = 0;
        this.listRef = React.createRef();
    }

    public componentDidUpdate(prevProps: IStoreSelectorLocationLinesProps): void {
        if (this.props.selectedStoreLocationId !== prevProps.selectedStoreLocationId) {
            this._scrollIntoView();
        }
    }

    public componentDidMount(): void {
        if (this.props.locations) {
            this._stores = this._sortStores(this.props.locations);
        }
        window.addEventListener('wheel', this.handleGlobalWheel, { passive: false });

        setTimeout(() => {
            if (this.listRef && this.listRef.current) {
                this.listRef.current.scrollTop = 0;
            }
        }, 0);
    }
    public componentWillUnmount(): void {
        window.removeEventListener('wheel', this.handleGlobalWheel);

        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
    }

    private handleGlobalWheel = (e: WheelEvent) => {
        const listElement = this.listRef.current;

        if (!listElement) {
            return;
        }

        const modalElement = listElement.closest('.msc-modal');
        const targetElement = e.target as HTMLElement;
        const isInsideModal = modalElement && modalElement.contains(targetElement);

        if (isInsideModal) {
            e.preventDefault();
            e.stopPropagation();
            listElement.scrollTop += e.deltaY;
        }
    };

    public render(): JSX.Element | null {
        if (!this._stores || this._stores.length === 0) {
            return null;
        }

        this.storeCounter = 0;
        const sortedStores = this._stores;

        // if (this.props.sortByLocation) {
        //     sortedStores = this._stores.sort(
        //         (a, b) => a.OrgUnitAvailability!.OrgUnitLocation!.Distance! - b.OrgUnitAvailability!.OrgUnitLocation!.Distance!
        //     );
        // }
        const removeStockBridgeStoreForCnC = sortedStores?.filter(
            store => store?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber !== 'S096'
        );

        const removeStockBridgeStoreForCheckStock = sortedStores?.filter(
            store => store?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber !== 'S096CNC'
        );
        this.listRef = this.listRef || React.createRef<HTMLDivElement>();
        //End Customization

        const selectedStore = this.props.locations?.find(
            store => store?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === this.props.selectedStoreLocationId
        );

        let imageUrl;
        const orgUnitNumber =
            selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber ||
            this.props.locations?.[0]?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber;
        const orgUnitName =
            selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName ||
            this.props.locations?.[0]?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName;
        const orgUnitNameSpelled: string | undefined = orgUnitName?.replace(/\s+/g, '_');

        const imagePath = `${orgUnitNumber}_${orgUnitNameSpelled}_location.png`;
        imageUrl = generateImageUrl(imagePath, this.props.apiSettings);
        imageUrl = imageUrl?.replace('fileName=/', 'fileName=');
        // Removed invalid React hook usage from class component

        return (
            <>
                <div className='ms-store-select__location-map'>
                    <Image src={imageUrl} gridSettings={this.props.gridSettings!} loadFailureBehavior='default' />
                </div>

                <div
                    ref={this.listRef}
                    className={classnames('ms-store-select__locations-list', { show: this.props.displayList })}
                    role='list'
                >
                    {!this.props.storeHeadingForLocalStore.includes('Check stock')
                        ? removeStockBridgeStoreForCnC.map((store, index) => {
                              return this._renderStoreLocation(store, index);
                          })
                        : removeStockBridgeStoreForCheckStock.map((store, index) => {
                              return this._renderStoreLocation(store, index);
                          })}
                </div>
            </>
        );
    }

    /**
     * On click Handler function.
     * @param orgUnitLocation -OrgUnit location.
     * @returns Click action on orgUnit Location.
     */

    private readonly onClickHandler = (orgUnitLocation: OrgUnitLocation | undefined) => () => {
        this.props.onClick(orgUnitLocation?.OrgUnitNumber);
    };

    private _renderStoreLocation(store: IFullOrgUnitAvailability, index: number): JSX.Element | undefined {
        const orgUnitLocation = store?.OrgUnitAvailability?.OrgUnitLocation;
        const selectedStoreLocationId = this.props.selectedStoreLocationId;
        const deliveryOptions = store?.OrgUnitPickUpDeliveryModes;
        const defaultAriaSetsize = 0;
        // VSI CUSTOMIZATION - HIDE Non C&C stores from C&C Modal
        if (!this.props.storeHeadingForLocalStore.includes('Check stock')) {
            if (!this.props.pickupStoreIds.includes(orgUnitLocation?.ChannelId)) {
                return;
            }
        }
        // VSI CUSTOMIZATION - HIDE Non C&C stores from C&C Modal
        if (orgUnitLocation) {
            ++this.storeCounter;
            return (
                <div
                    className={
                        this._isProductInStock(store?.OrgUnitAvailability?.ItemAvailabilities)
                            ? classnames('ms-store-select__location', {
                                  selected: this.props.selectedStoreLocationId
                                      ? this.props.selectedStoreLocationId === orgUnitLocation.OrgUnitNumber
                                      : this.props.alreadySelectedLocation
                                      ? this._isCurrentLocation(orgUnitLocation)
                                      : this.props.locations && this.props.locations.length > 0
                                      ? this.props.locations[0]?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber ===
                                        orgUnitLocation.OrgUnitNumber
                                      : false
                              })
                            : 'ms-store-select__location not-available'
                    }
                    role='tab'
                    key={index}
                    aria-setsize={ArrayExtensions.hasElements(this._stores) ? this._stores.length : defaultAriaSetsize}
                    aria-posinset={this.storeCounter}
                    onClick={this.onClickHandler(orgUnitLocation)}
                    ref={selectedStoreLocationId === orgUnitLocation.OrgUnitNumber ? this.selectedLocationRef : undefined}
                >
                    <StoreSelectorLocationLineItemComponent
                        location={orgUnitLocation}
                        storeHours={store.StoreHours}
                        resources={this._mapResources(this.props.resources)}
                        preferredDistanceUnit={this.props.preferredDistanceUnit!}
                        isCurrentLocation={this._isCurrentLocation(orgUnitLocation)}
                        isInStock={this._isProductInStock(store?.OrgUnitAvailability?.ItemAvailabilities)}
                        handlers={{
                            onSelected: this.props.onLocationSelected,
                            onSetAsPreferredStore: this.props.onSetAsPreferredStore,
                            onRemovePreferredStore: this.props.onRemovePreferredStore
                        }}
                        stockStatusLabel={this._stockInventoryLabel(store)}
                        storeLocatorView={this.props.storeLocatorView}
                        index={this.storeCounter.toString()}
                        preferredStoreLocationId={this.props.preferredStoreLocationId}
                        isPreferredStoreEnabled={this.props.isPreferredStoreEnabled}
                        showQuantity={this.props.showQuantity}
                        showStoreAvailableQuantity={this._getStoreAvailableQuantity(store)}
                        telemetryContent={this.props.telemetryContent}
                        isLocationDisabled={this.props.isLocationDisabled}
                        storePickUpOptionList={deliveryOptions?.DeliveryOptions}
                        productPickUpOptionList={this.props.productPickUpDeliveryOptions}
                        filteredPickupMode={this.props.filteredPickupMode}
                        isDisplayProduct={this.props.isDisplayProduct}
                        showCnC={this.props.showCnC}
                        CncPickupIds={this.props.pickupStoreIds}
                        localStoreHeading={this.props.storeHeadingForLocalStore}
                        config={this.props.config}
                        isSelected={
                            this.props.selectedStoreLocationId
                                ? this.props.selectedStoreLocationId === orgUnitLocation.OrgUnitNumber
                                : this.props.alreadySelectedLocation
                                ? this._isCurrentLocation(orgUnitLocation)
                                : this.props.locations && this.props.locations.length > 0
                                ? this.props.locations[0]?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber ===
                                  orgUnitLocation.OrgUnitNumber
                                : false
                        }
                        context={this.props.context}
                    />
                </div>
            );
        }
        return undefined;
    }

    private _sortStores(stores: IFullOrgUnitAvailability[]): IFullOrgUnitAvailability[] {
        const isPreferredStore = (value: IFullOrgUnitAvailability) =>
            this.props.isPreferredStoreEnabled &&
            this.props.preferredStoreLocationId &&
            value?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === this.props.preferredStoreLocationId;

        return [...stores.filter(isPreferredStore), ...stores.filter(store => !isPreferredStore(store))];
    }

    private _getStoreAvailableQuantity = (stores: IFullOrgUnitAvailability | undefined): number | undefined => {
        return (
            stores?.OrgUnitAvailability &&
            stores?.OrgUnitAvailability?.ItemAvailabilities &&
            stores?.OrgUnitAvailability?.ItemAvailabilities[0]?.AvailableQuantity
        );
    };
    private readonly _scrollIntoView = () => {
        const selectedLocationDiv = this.selectedLocationRef.current;
        if (selectedLocationDiv) {
            selectedLocationDiv.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    private readonly _isCurrentLocation = (location: OrgUnitLocation | undefined): boolean =>
        (this.props.alreadySelectedLocation && location && this.props.alreadySelectedLocation.OrgUnitNumber === location.OrgUnitNumber) ||
        false;

    private readonly _isProductInStock = (ItemAvailabilities: ItemAvailability[] | undefined): boolean => {
        if (!this.props.enableOutOfStockCheck) {
            return true;
        }

        if (ItemAvailabilities && ItemAvailabilities.length > 0) {
            return ItemAvailabilities[0].AvailableQuantity ? ItemAvailabilities[0].AvailableQuantity > 0 : false;
        }

        return false;
    };

    private readonly _stockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours?.ProductInventoryInformation && availabilityWithHours?.ProductInventoryInformation.length > 0) {
            for (let i = 0; i < availabilityWithHours.ProductInventoryInformation.length; i++) {
                if (
                    availabilityWithHours?.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    availabilityWithHours.ProductInventoryInformation[i].InventLocationId
                ) {
                    return availabilityWithHours.ProductInventoryInformation[i].StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    private readonly _mapResources = (resources: IDobbiesBuyboxStoreSelectorResources): IStoreSelectorLocationLineItemResources => {
        return {
            contactInfoHeader: resources.contactText,
            storeHoursHeader: resources.timeText,
            availabilityInfoHeader: resources.availabilityText,

            closedText: resources.storeHoursClosedText,
            outOfStock: resources.outOfStockText,
            inStock: resources.inStockText,

            currentLocation: resources.selectedStoreLocationText,
            selectLocation: resources.selectStoreText,
            selectLocationAriaLabelFormat: resources.selectStoreAriaFormatText,
            setAsPreferredStoreText: resources.setAsPreferredStoreText,
            setAsPreferredStoreTextAriaLabel: resources.setAsPreferredStoreTextAriaLabel,
            preferredStoreText: resources.preferredStoreText,
            preferredStoreAriaLabel: resources.preferredStoreAriaLabel,
            quantityText: resources.quantityText,

            days: {
                monday: resources.storeHoursMondayText,
                tuesday: resources.storeHoursTuesdayText,
                wednesday: resources.storeHoursWednesdayText,
                thursday: resources.storeHoursThursdayText,
                friday: resources.storeHoursFridayText,
                saturday: resources.storeHoursSaturdayText,
                sunday: resources.storeHoursSundayText,
                mondayFull: resources.storeHoursMondayFullText,
                tuesdayFull: resources.storeHoursTuesdayFullText,
                wednesdayFull: resources.storeHoursWednesdayFullText,
                thursdayFull: resources.storeHoursThursdayFullText,
                fridayFull: resources.storeHoursFridayFullText,
                saturdayFull: resources.storeHoursSaturdayFullText,
                sundayFull: resources.storeHoursSundayFullText
            }
        };
    };
}
